import React, { useState, type ReactNode } from 'react';
import { Modal, Spin } from 'antd';
import {
    CubeIcon,
    CurrencyDollarIcon,
    HeartIcon,
    ScalesIcon,
    SunIcon,
    TreeEvergreenIcon,
} from '@phosphor-icons/react';
import { useViewerStore } from '../../../../../store/store';
import { resolveTreeCutScenarioSnapshot } from '../utils/ai-layout';
import type { AiDesignsDataSource } from './DashboardAiDesignsSourceBadge';
import { formatTreeCutOptionCardLabel } from '../../../../../services/treeCartService';
import type { TreeCutPricingTable } from '../utils/finance';
import '../oneButton-css/OneButtonFormControls.css';

type ModalTree = {
    singleTreeWithWorthIt?: unknown[];
    multiTreeScenarios?: unknown[];
    baseWattage?: number;
    totalModulesBase?: number;
};

export type TreeScenarioTablesProps = {
    modalTreeData: ModalTree;
    layoutMetricsByKey: Record<string, any> | undefined;
    effectiveTreeLayoutKey: string;
    selectedRoofCombinationKey: string | null | undefined;
    activeLayoutRoofIds: string[] | undefined;
    activeModule: unknown;
    roofCards: any[];
    renderTreeCutGoodLeapPurchaseOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    renderTreeCutIgsPrimaryOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    renderTreeCutLightReachTotalEpcPaymentOnCard: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        options?: { hidePlaceholderUntilLoaded?: boolean },
    ) => ReactNode;
    solarUtilityData: unknown;
    triggerTreeCutScenarioPartnerPricing: (
        scenario: any,
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
    ) => void | Promise<void>;
    onOpenTreeScenarioFinancingPricing?: (
        scenario: { actualWattage?: number },
        table: TreeCutPricingTable,
        scenarioRowIndex: number,
        metrics: { systemKw: string; systemSizeKw?: number; estModules: number | undefined; roofCount: number },
    ) => void;
    isTreeCutScenarioSaved: (index: number) => boolean;
    handleSaveTreeCutScenario: (scenario: any, index: number) => void;
    applyingScenarioIndex: number | null | undefined;
    handleUseThisLayout: (index: number, layoutKey: string | null, scenarios: unknown) => void;
    treeCuttingForLayoutKey: string | null;
    treeScenariosByLayoutKey: Record<string, any> | undefined;
    setTreeCuttingPopupOpen: (open: boolean) => void;
    setTreeCuttingForLayoutKey: (key: string | null) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    sceneViewer: unknown;
    openAlternateCutCosts: (
        treeIds: string[],
        label: string,
        scenarioIndex: number,
        defaultCutCost: number | null,
        selectedOptionId?: string | null,
    ) => void;
    financingRoofCount: (layoutRc: number | undefined, roofsForRow: number | string | undefined) => number;
    theme: string;
};

function treeScenarioThumb(
    scenario: any,
    aiDesignsDataSource: AiDesignsDataSource | null,
): { url?: string; error?: string } {
    return resolveTreeCutScenarioSnapshot(scenario, aiDesignsDataSource);
}

function pricingLabelColor(theme: string): string {
    return theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)';
}

export function DashboardAiRailTreeCuttingSingleTable(props: TreeScenarioTablesProps) {
    const {
        modalTreeData,
        layoutMetricsByKey,
        effectiveTreeLayoutKey,
        selectedRoofCombinationKey,
        activeLayoutRoofIds,
        activeModule,
        roofCards,
        renderTreeCutGoodLeapPurchaseOnCard,
        renderTreeCutIgsPrimaryOnCard,
        renderTreeCutLightReachTotalEpcPaymentOnCard,
        solarUtilityData,
        triggerTreeCutScenarioPartnerPricing,
        onOpenTreeScenarioFinancingPricing,
        isTreeCutScenarioSaved,
        handleSaveTreeCutScenario,
        applyingScenarioIndex,
        handleUseThisLayout,
        treeCuttingForLayoutKey,
        treeScenariosByLayoutKey,
        setTreeCuttingPopupOpen,
        setTreeCuttingForLayoutKey,
        setIsDashboardOpen,
        sceneViewer,
        openAlternateCutCosts,
        financingRoofCount,
        theme,
    } = props;

    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const [snapshotPreview, setSnapshotPreview] = useState<{ label: string; url: string } | null>(null);
    const labelColor = pricingLabelColor(theme);

    return (
        <div className="editor-ai-tree-cutting-table-wrap editor-ai-tree-cutting-table-wrap--catalog-actions">
            <table className="editor-ai-tree-cutting-table">
                <thead>
                    <tr>
                        <th className="editor-ai-tree-cutting-th--image">Image</th>
                        <th className="editor-ai-tree-cutting-th--labels editor-ai-tree-cutting-th--scenario">Scenario</th>
                        <th className="editor-ai-tree-cutting-th--num">Modules</th>
                        <th className="editor-ai-tree-cutting-th--num">Roofs</th>
                        <th className="editor-ai-tree-cutting-th--num">Prod</th>
                        <th className="editor-ai-tree-cutting-th--num">System</th>
                        <th className="editor-ai-tree-cutting-th--labels">Cost / Gain</th>
                        <th className="editor-ai-tree-cutting-th--partner editor-ai-tree-cutting-th--pricing">Pricing</th>
                        <th className="editor-ai-tree-cutting-th--num editor-ai-tree-cutting-th--action">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        const singles = (modalTreeData.singleTreeWithWorthIt as any[]).map((s: any, i: number) => ({
                            ...s,
                            index: i,
                        }));
                        const baseW = modalTreeData.baseWattage ?? 0;

                        return singles
                            .filter((s: any) => !(s.treeIds && s.treeIds.length > 1))
                            .map((s: any) => {
                                const isBase = s.treeId == null;
                                const isRecommended = !isBase && !!s.worthIt;
                                const gainW = baseW ? (s.actualWattage ?? 0) - baseW : 0;
                                const gainPct = baseW ? (gainW / baseW) * 100 : 0;
                                const costNum =
                                    s.cutCost != null && Number.isFinite(Number(s.cutCost)) ? Number(s.cutCost) : null;

                                const layoutRoofMetrics = layoutMetricsByKey?.[effectiveTreeLayoutKey] || {};
                                const layoutRoofCount =
                                    typeof layoutRoofMetrics.roofCount === 'number' && Number.isFinite(layoutRoofMetrics.roofCount)
                                        ? layoutRoofMetrics.roofCount
                                        : effectiveTreeLayoutKey === selectedRoofCombinationKey
                                          ? (activeLayoutRoofIds?.length ?? undefined)
                                          : undefined;

                                const modulesForRow =
                                    (typeof modalTreeData.totalModulesBase === 'number' &&
                                    Number.isFinite(modalTreeData.totalModulesBase)
                                        ? modalTreeData.totalModulesBase
                                        : undefined) ?? '?';

                                const modulesNumericSingle =
                                    typeof modulesForRow === 'number' && Number.isFinite(modulesForRow) ? modulesForRow : undefined;

                                const modulePowerSingle = (activeModule as any)?.power ?? 0;
                                const systemSizeForRowSingle =
                                    modulesNumericSingle != null && modulePowerSingle > 0
                                        ? ((modulesNumericSingle * modulePowerSingle) / 1000).toFixed(2)
                                        : typeof s.actualWattage === 'number'
                                          ? (s.actualWattage / 1000).toFixed(2)
                                          : '?';

                                const roofsForRow =
                                    layoutRoofCount ||
                                    roofCards?.length ||
                                    (s.shadeWeights ?? []).length ||
                                    '?';

                                const prodCell =
                                    typeof s.actualWattage === 'number'
                                        ? `${Math.round(s.actualWattage).toLocaleString()} W`
                                        : '?';

                                const snap = treeScenarioThumb(s, aiDesignsDataSource);
                                const snapUrl = snap.url;
                                const snapError = snap.error;
                                const scenarioThumbLabel = isBase ? 'Ideal (no cuts)' : `Cut tree ${s.treeLabel ?? ''}`.trim();

                                return (
                                    <tr
                                        key={s.index}
                                        className={`editor-ai-tree-cutting-row${
                                            isRecommended ? ' editor-ai-tree-cutting-row--recommended' : ''
                                        }`}
                                    >
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--image">
                                            {snapUrl ? (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setSnapshotPreview({ label: scenarioThumbLabel, url: snapUrl })
                                                    }
                                                    aria-label={`View full layout image: ${scenarioThumbLabel}`}
                                                    title="View full image"
                                                    style={{
                                                        width: 60,
                                                        height: 40,
                                                        borderRadius: 6,
                                                        border: '1px solid rgba(148,163,184,0.35)',
                                                        overflow: 'hidden',
                                                        padding: 0,
                                                        background: 'transparent',
                                                        cursor: 'zoom-in',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <img
                                                        src={snapUrl}
                                                        alt=""
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                    />
                                                </button>
                                            ) : snapError ? (
                                                <span
                                                    className="editor-ai-tree-cutting-cell__thumb-missing"
                                                    title={snapError}
                                                    style={{ fontSize: 10, lineHeight: 1.25, color: 'rgba(148,163,184,0.95)' }}
                                                >
                                                    {snapError}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'rgba(148,163,184,0.9)' }}>?</span>
                                            )}
                                        </td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--scenario">
                                            <span className="editor-ai-tree-cutting-cell__title">
                                                {isBase ? (
                                                    <SunIcon
                                                        size={16}
                                                        weight="duotone"
                                                        className="editor-ai-tree-cutting-cell__icon editor-ai-tree-cutting-cell__icon--sun"
                                                    />
                                                ) : (
                                                    <TreeEvergreenIcon
                                                        size={16}
                                                        weight="duotone"
                                                        className="editor-ai-tree-cutting-cell__icon editor-ai-tree-cutting-cell__icon--tree"
                                                    />
                                                )}
                                                {isBase ? 'Ideal (no cuts)' : `Cut tree ${s.treeLabel}`}
                                            </span>
                                        </td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{modulesForRow}</td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{roofsForRow}</td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{prodCell}</td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">
                                            {systemSizeForRowSingle === '?' ? '?' : `${systemSizeForRowSingle} kW`}
                                        </td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--labels">
                                            <div className="editor-ai-tree-cutting-labels-stack">
                                                {isBase ? (
                                                    <span className="editor-ai-scenario-card__base-badge">Default</span>
                                                ) : (
                                                    <>
                                                        {costNum != null && (
                                                            <span className="editor-ai-tree-cutting-labels-cost">${Math.round(costNum)}</span>
                                                        )}
                                                        {!isBase ? (
                                                            <span
                                                                className="editor-ai-tree-cutting-labels-cut-option"
                                                                title="Tree cut option on card"
                                                            >
                                                                {formatTreeCutOptionCardLabel(s)}
                                                            </span>
                                                        ) : null}
                                                        <span
                                                            className="editor-ai-tree-cutting-labels-gain editor-ai-tree-cutting-gain"
                                                            style={{
                                                                color: gainPct >= 0 ? 'rgba(34, 197, 94, 0.95)' : 'rgba(239, 68, 68, 0.9)',
                                                            }}
                                                            title="Production gain %"
                                                        >
                                                            Production {gainPct >= 0 ? '+' : ''}
                                                            {gainPct.toFixed(2)}%
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--partner editor-ai-tree-cutting-cell--pricing-stack">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
                                                <span style={{ color: labelColor }}>
                                                    GL: {renderTreeCutGoodLeapPurchaseOnCard(s, 'single', s.index)}
                                                </span>
                                                <span style={{ color: labelColor }}>
                                                    IGS: {renderTreeCutIgsPrimaryOnCard(s, 'single', s.index)}
                                                </span>
                                                <span style={{ color: labelColor }}>
                                                    LR: {renderTreeCutLightReachTotalEpcPaymentOnCard(s, 'single', s.index)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--action">
                                            <div className="obd-ai-catalog-list__actions obd-ai-catalog-list__actions--dashboard-grid">
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--open3d"
                                                    disabled={applyingScenarioIndex === s.index}
                                                    title="Open in 3D viewer"
                                                    onClick={() => {
                                                        useViewerStore.setState({ isAiGeneratedDesign: true });
                                                        handleUseThisLayout(
                                                            s.index,
                                                            treeCuttingForLayoutKey,
                                                            treeScenariosByLayoutKey?.[effectiveTreeLayoutKey],
                                                        );
                                                        setTreeCuttingPopupOpen(false);
                                                        setTreeCuttingForLayoutKey(null);
                                                        setIsDashboardOpen(false);
                                                    }}
                                                >
                                                    {applyingScenarioIndex === s.index ? (
                                                        <Spin size="small" style={{ display: 'block' }} />
                                                    ) : (
                                                        <CubeIcon size={16} weight="duotone" aria-hidden />
                                                    )}
                                                </button>
                                                {!isBase && s.treeId ? (
                                                    <button
                                                        type="button"
                                                        className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--tree"
                                                        disabled={!sceneViewer}
                                                        title="Compare tree cut cost for other stump/haul options"
                                                        aria-label="Compare cut cost options"
                                                        onClick={() =>
                                                            openAlternateCutCosts(
                                                                [String(s.treeId)],
                                                                `Cut tree ${s.treeLabel ?? s.treeId}`,
                                                                s.index,
                                                                costNum,
                                                                s.cutCostOptionId ?? null,
                                                            )
                                                        }
                                                    >
                                                        <ScalesIcon size={16} weight="duotone" aria-hidden />
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--pricing"
                                                    disabled={!solarUtilityData}
                                                    title={
                                                        solarUtilityData
                                                            ? 'Open financing pricing and load GoodLeap / LightReach for this scenario'
                                                            : 'Load opportunity utility data on the dashboard first'
                                                    }
                                                    aria-label="Open financing pricing and partner rates"
                                                    onClick={() => {
                                                        void triggerTreeCutScenarioPartnerPricing(s, 'single', s.index);
                                                        onOpenTreeScenarioFinancingPricing?.(s, 'single', s.index, {
                                                            systemSizeKw:
                                                                modulesNumericSingle != null && modulePowerSingle > 0
                                                                    ? (modulesNumericSingle * modulePowerSingle) / 1000
                                                                    : typeof s.actualWattage === 'number'
                                                                      ? s.actualWattage / 1000
                                                                      : undefined,
                                                            systemKw:
                                                                systemSizeForRowSingle === '?'
                                                                    ? typeof s.actualWattage === 'number'
                                                                        ? (s.actualWattage / 1000).toFixed(2)
                                                                        : '0'
                                                                    : systemSizeForRowSingle,
                                                            estModules: modulesNumericSingle,
                                                            roofCount: financingRoofCount(layoutRoofCount, roofsForRow),
                                                        });
                                                    }}
                                                >
                                                    <CurrencyDollarIcon size={16} weight="duotone" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist${
                                                        isTreeCutScenarioSaved(s.index) ? ' obd-ai-catalog-list__icon-btn--saved' : ''
                                                    }`}
                                                    title={isTreeCutScenarioSaved(s.index) ? 'Already in wishlist' : 'Add to wishlist'}
                                                    aria-label="Save to wishlist"
                                                    onClick={() => handleSaveTreeCutScenario(s, s.index)}
                                                >
                                                    <HeartIcon
                                                        size={16}
                                                        weight={isTreeCutScenarioSaved(s.index) ? 'fill' : 'duotone'}
                                                        aria-hidden
                                                    />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            });
                    })()}
                </tbody>
            </table>
            {snapshotPreview ? (
                <Modal
                    open
                    onCancel={() => setSnapshotPreview(null)}
                    footer={null}
                    centered
                    destroyOnHidden
                    width="min(96vw, 1120px)"
                    zIndex={10250}
                    title={snapshotPreview.label}
                    rootClassName={`obd-ai-snapshot-preview-modal obd-theme-${theme}`}
                    classNames={{ body: 'obd-ai-snapshot-preview-modal__body' }}
                >
                    <div className="obd-ai-snapshot-preview-modal__scroll">
                        <img
                            src={snapshotPreview.url}
                            alt={snapshotPreview.label}
                            className="obd-ai-snapshot-preview-modal__img"
                        />
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

export function DashboardAiRailTreeCuttingMultiTable(props: TreeScenarioTablesProps) {
    const {
        modalTreeData,
        layoutMetricsByKey,
        effectiveTreeLayoutKey,
        selectedRoofCombinationKey,
        activeLayoutRoofIds,
        activeModule,
        roofCards,
        renderTreeCutGoodLeapPurchaseOnCard,
        renderTreeCutIgsPrimaryOnCard,
        renderTreeCutLightReachTotalEpcPaymentOnCard,
        solarUtilityData,
        triggerTreeCutScenarioPartnerPricing,
        onOpenTreeScenarioFinancingPricing,
        isTreeCutScenarioSaved,
        handleSaveTreeCutScenario,
        applyingScenarioIndex,
        handleUseThisLayout,
        treeCuttingForLayoutKey,
        treeScenariosByLayoutKey,
        setTreeCuttingPopupOpen,
        setTreeCuttingForLayoutKey,
        setIsDashboardOpen,
        sceneViewer,
        openAlternateCutCosts,
        financingRoofCount,
        theme,
    } = props;

    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const [snapshotPreview, setSnapshotPreview] = useState<{ label: string; url: string } | null>(null);
    const labelColor = pricingLabelColor(theme);

    return (
        <div className="editor-ai-tree-cutting-table-wrap editor-ai-tree-cutting-table-wrap--catalog-actions">
            <table className="editor-ai-tree-cutting-table">
                <thead>
                    <tr>
                        <th className="editor-ai-tree-cutting-th--image">Image</th>
                        <th className="editor-ai-tree-cutting-th--labels editor-ai-tree-cutting-th--scenario">Scenario</th>
                        <th className="editor-ai-tree-cutting-th--num">Modules</th>
                        <th className="editor-ai-tree-cutting-th--num">Roofs</th>
                        <th className="editor-ai-tree-cutting-th--num">Prod</th>
                        <th className="editor-ai-tree-cutting-th--num">System</th>
                        <th className="editor-ai-tree-cutting-th--labels">Cost / Gain</th>
                        <th className="editor-ai-tree-cutting-th--partner editor-ai-tree-cutting-th--pricing">Pricing</th>
                        <th className="editor-ai-tree-cutting-th--num editor-ai-tree-cutting-th--action">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        const multi = modalTreeData.multiTreeScenarios as any[];
                        const baseW = modalTreeData.baseWattage ?? 0;

                        return multi.map((m: any, multiIndex: number) => {
                            const enriched =
                                Array.isArray(modalTreeData.singleTreeWithWorthIt) && typeof m.index === 'number'
                                    ? (modalTreeData.singleTreeWithWorthIt as any[])[m.index]
                                    : undefined;
                            const isRecommended = enriched?.worthIt === true;

                            const modulesNumeric =
                                typeof modalTreeData.totalModulesBase === 'number' && Number.isFinite(modalTreeData.totalModulesBase)
                                    ? modalTreeData.totalModulesBase
                                    : undefined;
                            const modulesForRow = modulesNumeric ?? '?';

                            const layoutRoofMetrics = layoutMetricsByKey?.[effectiveTreeLayoutKey] || {};
                            const layoutRoofCount =
                                typeof layoutRoofMetrics.roofCount === 'number' && Number.isFinite(layoutRoofMetrics.roofCount)
                                    ? layoutRoofMetrics.roofCount
                                    : effectiveTreeLayoutKey === selectedRoofCombinationKey
                                      ? (activeLayoutRoofIds?.length ?? undefined)
                                      : undefined;

                            const roofsForRow =
                                layoutRoofCount ||
                                roofCards?.length ||
                                (m.shadeWeights ?? []).length ||
                                '?';

                            const modulePower = (activeModule as any)?.power ?? 0;
                            const systemSizeForRow =
                                modulesNumeric != null && modulePower > 0
                                    ? ((modulesNumeric * modulePower) / 1000).toFixed(2)
                                    : typeof m.actualWattage === 'number'
                                      ? (m.actualWattage / 1000).toFixed(2)
                                      : '?';

                            const gainW = baseW ? (m.actualWattage ?? 0) - baseW : 0;
                            const gainPct = baseW ? (gainW / baseW) * 100 : 0;

                            const prodCell =
                                typeof m.actualWattage === 'number'
                                    ? `${Math.round(m.actualWattage).toLocaleString()} W`
                                    : '?';

                            const costNum =
                                m.cutCost != null && Number.isFinite(Number(m.cutCost)) ? Number(m.cutCost) : null;

                            const snap = treeScenarioThumb(
                                enriched ?? m,
                                aiDesignsDataSource,
                            );
                            const snapUrl = snap.url;
                            const snapError = snap.error;
                            const scenarioThumbLabel =
                                m.treeIds && Array.isArray(m.treeIds) ? `Cut trees ${m.treeIds.join(', ')}` : 'Multi-tree scenario';

                            return (
                                <tr
                                    key={`multi-${multiIndex}`}
                                    className={`editor-ai-tree-cutting-row${
                                        isRecommended ? ' editor-ai-tree-cutting-row--recommended' : ''
                                    }`}
                                >
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--image">
                                        {snapUrl ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSnapshotPreview({ label: scenarioThumbLabel, url: snapUrl })
                                                }
                                                aria-label={`View full layout image: ${scenarioThumbLabel}`}
                                                title="View full image"
                                                style={{
                                                    width: 60,
                                                    height: 40,
                                                    borderRadius: 6,
                                                    border: '1px solid rgba(148,163,184,0.35)',
                                                    overflow: 'hidden',
                                                    padding: 0,
                                                    background: 'transparent',
                                                    cursor: 'zoom-in',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <img
                                                    src={snapUrl}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                />
                                            </button>
                                        ) : snapError ? (
                                            <span
                                                className="editor-ai-tree-cutting-cell__thumb-missing"
                                                title={snapError}
                                                style={{ fontSize: 10, lineHeight: 1.25, color: 'rgba(148,163,184,0.95)' }}
                                            >
                                                {snapError}
                                            </span>
                                        ) : (
                                            <span style={{ color: 'rgba(148,163,184,0.9)' }}>?</span>
                                        )}
                                    </td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--scenario">
                                        <span className="editor-ai-tree-cutting-cell__title">
                                            <TreeEvergreenIcon
                                                size={16}
                                                weight="duotone"
                                                className="editor-ai-tree-cutting-cell__icon editor-ai-tree-cutting-cell__icon--tree"
                                            />
                                            Cut {m.treeIds && Array.isArray(m.treeIds) ? m.treeIds.join(', ') : '?'}
                                        </span>
                                    </td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{modulesForRow}</td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{roofsForRow}</td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">{prodCell}</td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--num">
                                        {systemSizeForRow === '?' ? '?' : `${systemSizeForRow} kW`}
                                    </td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--labels">
                                        <div className="editor-ai-tree-cutting-labels-stack">
                                            <span className="editor-ai-tree-cutting-labels-cost">
                                                {costNum != null ? `$${Math.round(costNum)}` : '?'}
                                            </span>
                                            <span className="editor-ai-tree-cutting-labels-cut-option" title="Tree cut option on card">
                                                {formatTreeCutOptionCardLabel(m)}
                                            </span>
                                            <span
                                                className="editor-ai-tree-cutting-labels-gain editor-ai-tree-cutting-gain"
                                                style={{
                                                    color: gainPct >= 0 ? 'rgba(34, 197, 94, 0.95)' : 'rgba(239, 68, 68, 0.9)',
                                                }}
                                                title="Production gain %"
                                            >
                                                Production {gainPct >= 0 ? '+' : ''}
                                                {gainPct.toFixed(2)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--partner editor-ai-tree-cutting-cell--pricing-stack">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
                                            <span style={{ color: labelColor }}>
                                                GL: {renderTreeCutGoodLeapPurchaseOnCard(m, 'multi', multiIndex)}
                                            </span>
                                            <span style={{ color: labelColor }}>
                                                IGS: {renderTreeCutIgsPrimaryOnCard(m, 'multi', multiIndex)}
                                            </span>
                                            <span style={{ color: labelColor }}>
                                                LR: {renderTreeCutLightReachTotalEpcPaymentOnCard(m, 'multi', multiIndex)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="editor-ai-tree-cutting-cell editor-ai-tree-cutting-cell--action">
                                        <div className="obd-ai-catalog-list__actions obd-ai-catalog-list__actions--dashboard-grid">
                                            <button
                                                type="button"
                                                className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--open3d"
                                                disabled={applyingScenarioIndex === m.index}
                                                title="Open in 3D viewer"
                                                onClick={() => {
                                                    useViewerStore.setState({ isAiGeneratedDesign: true });
                                                    handleUseThisLayout(
                                                        m.index,
                                                        treeCuttingForLayoutKey,
                                                        treeScenariosByLayoutKey?.[effectiveTreeLayoutKey],
                                                    );
                                                    setTreeCuttingPopupOpen(false);
                                                    setTreeCuttingForLayoutKey(null);
                                                    setIsDashboardOpen(false);
                                                }}
                                            >
                                                {applyingScenarioIndex === m.index ? (
                                                    <Spin size="small" style={{ display: 'block' }} />
                                                ) : (
                                                    <CubeIcon size={16} weight="duotone" aria-hidden />
                                                )}
                                            </button>
                                            {m.treeIds && Array.isArray(m.treeIds) && m.treeIds.length > 0 ? (
                                                <button
                                                    type="button"
                                                    className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--tree"
                                                    disabled={!sceneViewer}
                                                    title="Compare tree cut cost for other stump/haul options"
                                                    aria-label="Compare cut cost options"
                                                    onClick={() =>
                                                        openAlternateCutCosts(
                                                            m.treeIds.map(String),
                                                            `Cut trees ${m.treeIds.join(', ')}`,
                                                            m.index,
                                                            costNum,
                                                            m.cutCostOptionId ?? null,
                                                        )
                                                    }
                                                >
                                                    <ScalesIcon size={16} weight="duotone" aria-hidden />
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--pricing"
                                                disabled={!solarUtilityData}
                                                title={
                                                    solarUtilityData
                                                        ? 'Open financing pricing and load GoodLeap / LightReach for this scenario'
                                                        : 'Load opportunity utility data on the dashboard first'
                                                }
                                                aria-label="Open financing pricing and partner rates"
                                                onClick={() => {
                                                    void triggerTreeCutScenarioPartnerPricing(m, 'multi', multiIndex);
                                                    onOpenTreeScenarioFinancingPricing?.(m, 'multi', multiIndex, {
                                                        systemSizeKw:
                                                            modulesNumeric != null && modulePower > 0
                                                                ? (modulesNumeric * modulePower) / 1000
                                                                : typeof m.actualWattage === 'number'
                                                                  ? m.actualWattage / 1000
                                                                  : undefined,
                                                        systemKw:
                                                            systemSizeForRow === '?'
                                                                ? typeof m.actualWattage === 'number'
                                                                    ? (m.actualWattage / 1000).toFixed(2)
                                                                    : '0'
                                                                : systemSizeForRow,
                                                        estModules: modulesNumeric,
                                                        roofCount: financingRoofCount(layoutRoofCount, roofsForRow),
                                                    });
                                                }}
                                            >
                                                <CurrencyDollarIcon size={16} weight="duotone" aria-hidden />
                                            </button>
                                            <button
                                                type="button"
                                                className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist${
                                                    isTreeCutScenarioSaved(m.index) ? ' obd-ai-catalog-list__icon-btn--saved' : ''
                                                }`}
                                                title={isTreeCutScenarioSaved(m.index) ? 'Already in wishlist' : 'Add to wishlist'}
                                                aria-label="Save to wishlist"
                                                onClick={() => handleSaveTreeCutScenario(m, m.index)}
                                            >
                                                <HeartIcon
                                                    size={16}
                                                    weight={isTreeCutScenarioSaved(m.index) ? 'fill' : 'duotone'}
                                                    aria-hidden
                                                />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        });
                    })()}
                </tbody>
            </table>
            {snapshotPreview ? (
                <Modal
                    open
                    onCancel={() => setSnapshotPreview(null)}
                    footer={null}
                    centered
                    destroyOnHidden
                    width="min(96vw, 1120px)"
                    zIndex={10250}
                    title={snapshotPreview.label}
                    rootClassName={`obd-ai-snapshot-preview-modal obd-theme-${theme}`}
                    classNames={{ body: 'obd-ai-snapshot-preview-modal__body' }}
                >
                    <div className="obd-ai-snapshot-preview-modal__scroll">
                        <img
                            src={snapshotPreview.url}
                            alt={snapshotPreview.label}
                            className="obd-ai-snapshot-preview-modal__img"
                        />
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}
